import { ActionIcon, Button, Checkbox, Menu, TextInput } from '@mantine/core';
import { IconDownload, IconSearch, IconX } from '@tabler/icons-react';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { LoadingState } from '@monkvision/common';
import { UseInspectionListFiltersResult } from '../../hooks';
import styles from './InspectionListFilters.module.css';
import { STATUS_TRANSLATION_KEYS } from '../DashboardDrawer';
import { DashboardInspectionStatus } from '../../hooks/useInspectionList/types';
import { DashboardInspectionListFilters } from '../../hooks/useInspectionListFilters/types';
import { DateFilter } from '../DateFilter';

const DEFAULT_STATUS_FILTERS = Object.values(DashboardInspectionStatus).reduce<
  Record<DashboardInspectionStatus, boolean>
>((prev, value) => ({ ...prev, [value]: false }), {} as Record<DashboardInspectionStatus, boolean>);

export interface InspectionListFiltersProps
  extends Pick<
    UseInspectionListFiltersResult,
    'filterByDate' | 'filterBySearchInput' | 'filterByStatuses'
  > {
  title: string;
  filters: DashboardInspectionListFilters;
  showStatusFilter: boolean;
  isListLoading: boolean;
  onExportToCsv: () => void;
  exportToCsvLoading: LoadingState;
}

function areSearchInputEquals(a: string | null | undefined, b: string | null | undefined): boolean {
  return !a || !b ? !a === !b : a === b;
}

export function InspectionListFilters({
  title,
  filters,
  showStatusFilter,
  filterByDate,
  filterBySearchInput,
  filterByStatuses,
  isListLoading,
  onExportToCsv,
  exportToCsvLoading,
}: InspectionListFiltersProps) {
  const statusFiltersCurrentValue = useMemo(
    () =>
      Object.values(DashboardInspectionStatus).reduce(
        (prev, curr) => ({ ...prev, [curr]: !!filters.statuses?.includes(curr) }),
        {} as Record<DashboardInspectionStatus, boolean>,
      ),
    [filters?.statuses],
  );
  const [statusFilters, setStatusFilters] = useState(statusFiltersCurrentValue);
  const [searchInput, setSearchInput] = useState(filters.searchInput);
  const [statusFiltersMenuOpened, setStatusFiltersMenuOpened] = useState(false);
  const { t } = useTranslation();

  const applyStatusFiltersAvailable = useMemo(
    () =>
      Object.values(DashboardInspectionStatus).some(
        (status) => statusFiltersCurrentValue[status] !== statusFilters[status],
      ),
    [statusFiltersCurrentValue, statusFilters],
  );

  const handleStatusMenuClosed = () => {
    setStatusFilters(statusFiltersCurrentValue);
  };

  const closeStatusFiltersMenu = () => {
    setStatusFiltersMenuOpened(false);
    handleStatusMenuClosed();
  };

  const clearSearchInput = () => {
    setSearchInput('');
    filterBySearchInput(null);
  };

  const applyStatusFiltersMenu = () => {
    const checkedValues = Object.values(DashboardInspectionStatus).filter(
      (status) => statusFilters[status],
    );
    filterByStatuses(checkedValues.length > 0 ? checkedValues : null);
    setStatusFiltersMenuOpened(false);
  };

  const selectStatusFilter = (status: DashboardInspectionStatus) => {
    setStatusFilters((value) => ({
      ...DEFAULT_STATUS_FILTERS,
      [status]: !value[status],
    }));
  };

  useEffect(() => {
    if (filters.statuses === null) {
      applyStatusFiltersMenu();
    }
  }, [filters?.statuses]);

  return (
    <div className={styles['container']}>
      <div className={styles['title']}>{title}</div>
      <div className={styles['filters']}>
        <div className={styles['left']}>
          <TextInput
            icon={<IconSearch />}
            rightSection={
              searchInput && (
                <ActionIcon variant='transparent' color='dark' onClick={clearSearchInput}>
                  <IconX size={18} />
                </ActionIcon>
              )
            }
            variant='unstyled'
            placeholder={t('inspectionList.searchPlaceHolder')}
            classNames={{
              root: styles['inputRoot'],
              wrapper: styles['inputWrapper'],
              input: clsx(
                searchInput &&
                  areSearchInputEquals(searchInput, filters.searchInput) &&
                  styles['inputActive'],
              ),
            }}
            value={searchInput ?? ''}
            onChange={(e) => setSearchInput(e.target.value)}
            disabled={isListLoading}
          />
          <Button
            variant='filled'
            color='dark'
            classNames={{ root: styles['middleButton'] }}
            disabled={isListLoading || areSearchInputEquals(searchInput, filters.searchInput)}
            onClick={() => filterBySearchInput(searchInput)}
          >
            {t('inspectionList.search')}
          </Button>
          {showStatusFilter && (
            <Menu
              shadow='md'
              opened={statusFiltersMenuOpened}
              onChange={setStatusFiltersMenuOpened}
              closeOnItemClick={false}
              onClose={handleStatusMenuClosed}
            >
              <Menu.Target>
                <Button
                  variant={filters.statuses ? 'filled' : 'outline'}
                  color='dark'
                  classNames={{ root: styles['middleButton'] }}
                  disabled={isListLoading}
                >
                  {t('inspectionList.status')}
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>{t('inspectionList.filterByStatus')}</Menu.Label>
                {Object.values(DashboardInspectionStatus).map((status) => (
                  <Menu.Item key={status} onClick={() => selectStatusFilter(status)}>
                    <Checkbox
                      label={t(STATUS_TRANSLATION_KEYS[status])}
                      checked={statusFilters[status]}
                      classNames={{ root: styles['statusCheckbox'] }}
                      color='dark'
                    />
                  </Menu.Item>
                ))}
                <Menu.Divider />
                <div className={styles['statusesButtons']}>
                  <Button variant='subtle' color='dark' onClick={closeStatusFiltersMenu}>
                    {t('inspectionList.cancel')}
                  </Button>
                  <Button
                    variant='filled'
                    color='dark'
                    onClick={applyStatusFiltersMenu}
                    disabled={!applyStatusFiltersAvailable}
                  >
                    {t('inspectionList.apply')}
                  </Button>
                </div>
              </Menu.Dropdown>
            </Menu>
          )}
          <DateFilter filters={filters} filterByDate={filterByDate} isListLoading={isListLoading} />
        </div>
        <div className={styles['right']}>
          <Button
            variant='filled'
            color='dark'
            leftIcon={<IconDownload size={14} />}
            classNames={{ root: styles['middleButton'] }}
            disabled={isListLoading || exportToCsvLoading.isLoading}
            loading={exportToCsvLoading.isLoading}
            onClick={onExportToCsv}
          >
            {t('inspectionList.exportCsv')}
          </Button>
        </div>
      </div>
    </div>
  );
}
